/**
 * RoadClassLegend — persistent floating legend for the Major Roads overlay.
 *
 * Renders in the bottom-left of the field-ops map whenever showMajorRoads is on,
 * regardless of whether the Layers popover is open. Shows a loading spinner
 * while the Overpass fetch is in flight (Overpass can take 5–10s on a cold pan).
 *
 * Mounted at the TurfMap wrapper level (outside the react-map-gl <Map>) so it
 * doesn't need map context — it just observes overlay state from the store.
 */

import { Loader2 } from 'lucide-react';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';

// Must match HIGHWAY_COLOR in RoadClassOverlay.tsx
// Labels lead with the user-meaningful interpretation, not the OSM jargon.
const ROAD_CLASS_COLORS = [
  { color: '#dc2626', label: 'Heaviest traffic', osmClass: 'Primary' },
  { color: '#f97316', label: 'Medium traffic', osmClass: 'Secondary' },
  { color: '#eab308', label: 'Lighter traffic', osmClass: 'Tertiary' },
] as const;

export function RoadClassLegend() {
  const showMajorRoads = useFieldOpsStore((s) => s.showMajorRoads);
  const isLoading = useFieldOpsStore((s) => s.isLoadingMajorRoads);

  if (!showMajorRoads) return null;

  return (
    <div className="absolute bottom-20 left-4 z-10 pointer-events-none select-none">
      <div className="bg-white/95 backdrop-blur-sm rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.1)] px-3 py-2 space-y-1.5 min-w-[160px]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
            Major Roads
          </div>
          {isLoading && (
            <div className="flex items-center gap-1 text-[10px] text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Loading…</span>
            </div>
          )}
        </div>

        <div className="space-y-0.5">
          {ROAD_CLASS_COLORS.map(({ color, label, osmClass }) => (
            <div key={label} className="flex items-center gap-2">
              <div className="h-1 w-6 rounded-sm" style={{ backgroundColor: color }} />
              <span className="text-[11px] text-gray-700">{label}</span>
              <span className="text-[10px] text-gray-400 ml-auto">{osmClass}</span>
            </div>
          ))}
        </div>

        <div className="pt-1 border-t border-gray-100 text-[10px] text-gray-500 leading-snug">
          Bold lines = state/US/county routes — most reliable signal
        </div>
      </div>
    </div>
  );
}
